import React, { JSX } from 'react';
import { CellProps } from 'react-table';
import { Badge } from 'reactstrap';
import { Tooltip } from '@mui/material';
import {
  ChecklistTemplateType,
  ServiceTicketStatus,
  ServiceTicketType,
  ServiceTicketVariant,
  SiteReadinessEvaluationReason,
  SiteReadinessEvaluationStatus,
} from '../../types/service-calls/service-calls.type';
import CustomCell from '../../components/Table/Cell/CustomCell/CustomCell';
import { getLocationListPath, getOrganizationListPath } from '../../routes/Router';
import { ColumnType } from '../../types/other';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

export const SERVICE_TICKET_VARIANT_NUMERIC_TO_ID: Record<number, ServiceTicketVariant> = {
  0: 'INSTALLATION',
  1: 'BREAKFIX',
  2: 'CUSTOM',
  3: 'SITE_SURVEY',
  4: 'VIRTUAL_SITE_SURVEY',
};

// Service ticket status options
export const serviceTicketStatusOptions = [
  { id: 'requested', name: 'Requested' },
  { id: 'scheduled', name: 'Scheduled' },
  { id: 'site_readiness_scheduled', name: 'Site Readiness Scheduled' },
  { id: 'site_readiness_completed', name: 'Site Readiness Completed' },
  { id: 'site_survey_required', name: 'Site Survey Required' },
  { id: 'site_survey_completed', name: 'Site Survey Completed' },
  { id: 'reschedule_needed', name: 'Reschedule Needed' },
  { id: 'in_progress', name: 'In Progress' },
  { id: 'revisit_needed', name: 'Revisit Needed' },
  { id: 'on_hold', name: 'On Hold' },
  { id: 'completed', name: 'Completed' },
  { id: 'cancelled', name: 'Cancelled' },
];

export const INSTALLATION_ONLY_SERVICE_TICKET_STATUSES: ReadonlyArray<ServiceTicketStatus> = [
  'site_readiness_scheduled',
  'site_readiness_completed',
  'site_survey_required',
  'site_survey_completed',
];

export const isInstallationOnlyServiceTicketStatus = (status: string | null | undefined): boolean =>
  !!status && INSTALLATION_ONLY_SERVICE_TICKET_STATUSES.includes(status as ServiceTicketStatus);

const LINKED_SITE_SURVEY_BLOCKED_STATUSES: ReadonlySet<ServiceTicketStatus> = new Set(['cancelled', 'completed']);

export const canCreateLinkedSiteSurveyFromInstallation = (
  serviceTicket: ServiceTicketType | null | undefined
): boolean => {
  if (!serviceTicket || serviceTicket.serviceVariant !== 'INSTALLATION') {
    return false;
  }

  if (serviceTicket.associatedTelcobillServiceOrderId == null) {
    return false;
  }

  if (serviceTicket.associatedSiteSurveyServiceCallId != null) {
    return false;
  }

  return !LINKED_SITE_SURVEY_BLOCKED_STATUSES.has(serviceTicket.status);
};

// Service ticket variant options
export const serviceTicketVariantOptions = [
  { id: 'CUSTOM', name: 'Custom' },
  { id: 'BREAKFIX', name: 'Break Fix' },
  { id: 'INSTALLATION', name: 'Installation' },
  { id: 'SITE_SURVEY', name: 'Site Survey' },
  { id: 'VIRTUAL_SITE_SURVEY', name: 'Virtual Site Survey' },
];

export const checklistTemplateLineItemTypeOptions: Array<{ id: 'checkbox' | 'yes_no'; name: string }> = [
  { id: 'checkbox', name: 'Checkbox' },
  { id: 'yes_no', name: 'Yes / No' },
];

export const checklistTemplateLineItemRequiredOptions: Array<{ id: boolean; name: string }> = [
  { id: true, name: 'Yes' },
  { id: false, name: 'No' },
];

export type TimeSlotOption = { readonly id: number; readonly name: string };

export const requestedTimeSlotOptions: ReadonlyArray<TimeSlotOption> = [
  { id: 1, name: '8:00 AM - 12:00 PM' },
  { id: 2, name: '12:00 PM - 4:00 PM' },
  { id: 3, name: '4:00 PM - 8:00 PM (Additional charges apply)' },
  { id: 4, name: '8:00 PM - 12:00 AM (Additional charges apply)' },
];

/** @deprecated Used only to render historical scheduled slot IDs (1-4). */
const legacyScheduledTimeSlotOptions: ReadonlyArray<TimeSlotOption> = [
  { id: 1, name: '8:00 AM - 12:00 PM' },
  { id: 2, name: '12:00 PM - 4:00 PM' },
  { id: 3, name: '4:00 PM - 8:00 PM (Additional charges apply)' },
  { id: 4, name: '8:00 PM - 12:00 AM (Additional charges apply)' },
];

export const scheduledTimeSlotOptions: ReadonlyArray<TimeSlotOption> = [
  { id: 11, name: '8:00 AM - 10:00 AM' },
  { id: 12, name: '9:00 AM - 11:00 AM' },
  { id: 13, name: '10:00 AM - 12:00 PM' },
  { id: 14, name: '11:00 AM - 1:00 PM' },
  { id: 15, name: '12:00 PM - 2:00 PM' },
  { id: 16, name: '1:00 PM - 3:00 PM' },
  { id: 17, name: '2:00 PM - 4:00 PM' },
  { id: 18, name: '3:00 PM - 5:00 PM' },
  { id: 19, name: '4:00 PM - 6:00 PM (Additional charges apply)' },
  { id: 20, name: '5:00 PM - 7:00 PM (Additional charges apply)' },
  { id: 21, name: '6:00 PM - 8:00 PM (Additional charges apply)' },
  { id: 22, name: '7:00 PM - 9:00 PM (Additional charges apply)' },
  { id: 23, name: '8:00 PM - 10:00 PM (Additional charges apply)' },
  { id: 24, name: '9:00 PM - 11:00 PM (Additional charges apply)' },
  { id: 25, name: '10:00 PM - 12:00 AM (Additional charges apply)' },
];

export const findRequestedTimeSlot = (timeOptionId: number | null | undefined) =>
  timeOptionId == null ? undefined : requestedTimeSlotOptions.find(opt => opt.id === timeOptionId);

export const findScheduledTimeSlot = (timeOptionId: number | null | undefined) => {
  if (timeOptionId == null) return undefined;
  return (
    scheduledTimeSlotOptions.find(opt => opt.id === timeOptionId) ??
    legacyScheduledTimeSlotOptions.find(opt => opt.id === timeOptionId)
  );
};

// Helper function to get status label
// Service ticket at-risk filter options
export const serviceTicketAtRiskOptions = [
  { id: 'true', name: 'At Risk Only' },
  { id: 'false', name: 'Not At Risk' },
];

export const serviceTicketSiteReadinessOptions = [
  { id: SiteReadinessEvaluationStatus.Ready, name: 'Ready' },
  { id: SiteReadinessEvaluationStatus.NotReady, name: 'Not Ready' },
  { id: SiteReadinessEvaluationStatus.NotStarted, name: 'Not Started' },
];

export const getSiteReadinessStatusLabel = (status?: SiteReadinessEvaluationStatus): string =>
  serviceTicketSiteReadinessOptions.find(option => option.id === status)?.name ?? 'Not Started';

export const getSiteReadinessStatusColor = (status?: SiteReadinessEvaluationStatus): string => {
  if (status === SiteReadinessEvaluationStatus.Ready) return 'success';
  if (status === SiteReadinessEvaluationStatus.NotReady) return 'warning';
  return 'secondary';
};

export const siteReadinessReasonLabels: Record<SiteReadinessEvaluationReason, string> = {
  [SiteReadinessEvaluationReason.BuildingType]: 'Building type',
  [SiteReadinessEvaluationReason.WallMountingMaterial]: 'Wall mounting material',
  [SiteReadinessEvaluationReason.PermanentPower]: 'Permanent power availability',
  [SiteReadinessEvaluationReason.TelcoRoomPhotos]: 'Telco room photos',
  [SiteReadinessEvaluationReason.CellSignals]: 'Cell signal strength',
  [SiteReadinessEvaluationReason.IssuesOrConcerns]: 'Issues or concerns',
  [SiteReadinessEvaluationReason.NearestPowerLocation]: 'Nearest power location',
  [SiteReadinessEvaluationReason.NoPower]: 'No permanent power within 6ft (hard stop)',
  [SiteReadinessEvaluationReason.PoorCellSignal]: 'No Good/Fair cell signal (hard stop)',
};

export const getStatusLabel = (status: ServiceTicketStatus): string => {
  const statusOption = serviceTicketStatusOptions.find(option => option.id === status);
  return statusOption ? statusOption.name : 'Unknown';
};

export const getVariantLabel = (variant: ServiceTicketVariant): string => {
  const variantOption = serviceTicketVariantOptions.find(option => option.id === variant);
  return variantOption ? variantOption.name : 'Unknown';
};

export const formatChecklistTemplateServiceVariant = (v: ChecklistTemplateType['serviceVariant']): string => {
  if (typeof v === 'number') {
    const key = SERVICE_TICKET_VARIANT_NUMERIC_TO_ID[v];
    return key ? getVariantLabel(key) : String(v);
  }
  if (typeof v === 'string') {
    const fromOptions = getVariantLabel(v as ServiceTicketVariant);
    return fromOptions !== 'Unknown' ? fromOptions : v.replace(/_/g, ' ');
  }
  return '—';
};

export const formatTechnicianInfo = (technician: any): React.ReactElement | string => {
  if (!technician) return 'No technician assigned';

  const phoneDisplay = technician.phoneNumber ? technician.phoneNumber.number : 'N/A';

  return (
    <div style={{ fontSize: '0.9em' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
        {technician.firstName} {technician.lastName}
      </div>
      {technician?.email && (
        <div style={{ color: '#666', marginBottom: '2px' }}>
          <span style={{ marginRight: '5px', fontWeight: 'bold' }}>Email:</span>
          <span>{technician.email}</span>
        </div>
      )}
      <div style={{ color: '#666' }}>
        <span style={{ marginRight: '5px', fontWeight: 'bold' }}>Phone:</span>
        <span>{phoneDisplay}</span>
      </div>
    </div>
  );
};

export const formatTechniciansInfo = (
  technicians: any[] | undefined,
  singleTechnician: any
): React.ReactElement | string => {
  if (technicians && Array.isArray(technicians) && technicians.length > 0) {
    return (
      <div style={{ fontSize: '0.9em' }}>
        {technicians.map((tech, index) => {
          const phoneDisplay = tech.phoneNumber ? tech.phoneNumber.number : 'N/A';
          return (
            <div key={tech.id || index} style={{ marginBottom: index < technicians.length - 1 ? '12px' : '0' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {tech.firstName} {tech.lastName}
              </div>
              {tech?.email && (
                <div style={{ color: '#666', marginBottom: '2px' }}>
                  <span style={{ marginRight: '5px', fontWeight: 'bold' }}>Email:</span>
                  <span>{tech.email}</span>
                </div>
              )}
              <div style={{ color: '#666' }}>
                <span style={{ marginRight: '5px', fontWeight: 'bold' }}>Phone:</span>
                <span>{phoneDisplay}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (singleTechnician) {
    return formatTechnicianInfo(singleTechnician);
  }

  return 'No technician assigned';
};

export const serviceTicketListColumns: ColumnType<ServiceTicketType>[] = [
  {
    Header: 'Number',
    accessor: 'id',
    minCellWidth: '80px',
    filterKey: 'service_ticket.id',
    textAlign: 'right',
    Cell: ({ value }: CellProps<ServiceTicketType, number>): JSX.Element => (
      <CustomCell value={`SC-${value}`} textAlign='right' />
    ),
  },
  {
    Header: 'Type',
    accessor: 'serviceVariant',
    minCellWidth: '120px',
    filterKey: 'service_ticket.serviceVariant',
    Cell: ({ value }: any) => getVariantLabel(value),
  },
  {
    Header: 'Requested Date',
    accessor: 'requestedDateTimes',
    minCellWidth: '150px',
    filterKey: 'requestedDateTimes.date',
    Cell: ({ value }: any) => {
      if (Array.isArray(value) && value.length > 0 && value[0].date) {
        const timeSlot = findRequestedTimeSlot(value[0].timeOptionId);
        return (
          <div>
            <div>{formatDate(value[0].date)}</div>
            <small className='text-muted'>{timeSlot ? timeSlot.name : 'No time specified'}</small>
          </div>
        );
      }
      return 'Not scheduled';
    },
  },
  {
    Header: 'Provider',
    accessor: 'resellerOrganization',
    minCellWidth: '120px',
    filterKey: 'parentOrg.name',
    type: 'link',
    Cell: (value: CellProps<ServiceTicketType, any>): React.ReactElement => {
      const resellerOrganization = value.cell.row.original?.resellerOrganization;
      return (
        <CustomCell
          link={`${process.env.PUBLIC_URL}${getOrganizationListPath}/${resellerOrganization?.id}`}
          value={resellerOrganization?.name || ''}
          type='link'
        />
      );
    },
  },
  {
    Header: 'Organization',
    accessor: 'org',
    minCellWidth: '120px',
    type: 'link',
    filterKey: 'org.name',
    Cell: (value: CellProps<ServiceTicketType, any>): React.ReactElement => {
      const org = value.cell.row.original?.org;
      return (
        <CustomCell
          link={`${process.env.PUBLIC_URL}${getOrganizationListPath}/${org?.id}`}
          value={org?.name || ''}
          type='link'
        />
      );
    },
  },
  {
    Header: 'Service Organization',
    accessor: row => row.serviceOrganization?.name || '—',
    id: 'serviceOrganization',
    minCellWidth: '160px',
    filterKey: 'serviceOrganization.name',
  },
  {
    Header: 'Location',
    accessor: 'location',
    minCellWidth: '120px',
    type: 'link',
    Cell: (value: CellProps<ServiceTicketType, any>): React.ReactElement => {
      const location = value.cell.row.original?.location;
      return (
        <CustomCell
          link={`${process.env.PUBLIC_URL}${getLocationListPath}/${location?.id}`}
          value={location?.name || ''}
          type='link'
        />
      );
    },
    filterKey: 'service_ticket.locationName',
  },
  {
    Header: 'Timezone',
    accessor: 'location.timezone',
    filterKey: 'service_ticket.locationTimezone',
  },
  {
    Header: 'Scheduled For',
    accessor: 'scheduledForDate',
    minCellWidth: '150px',
    filterKey: 'service_ticket.scheduledForDate',
    Cell: ({ value, row }: any) => {
      if (value && row.original.scheduledForTimeOptionId) {
        const timeSlot = findScheduledTimeSlot(row.original.scheduledForTimeOptionId);
        return (
          <div>
            <div>{formatDate(value)}</div>
            <small className='text-muted'>{timeSlot ? timeSlot.name : 'No time specified'}</small>
          </div>
        );
      }
      return 'Not scheduled';
    },
  },
  {
    Header: 'Technician',
    accessor: 'technician',
    minCellWidth: '200px',
    filterKey: 'service_ticket.technician',
    Cell: ({ value, row }: any) => {
      const technicians = row.original?.technicians;
      return formatTechniciansInfo(technicians, value);
    },
  },
  {
    Header: 'Last Updated',
    accessor: 'lastUpdatedAt',
    minCellWidth: '150px',
    filterKey: 'service_ticket.lastUpdatedAt',
    Cell: ({ value }: any) => formatDateTime(value),
  },
  {
    Header: 'Status',
    accessor: 'status',
    minCellWidth: '160px',
    filterKey: 'service_ticket.status',
    Cell: ({ value, row }: any) => {
      const atRisk = row.original?.atRisk;
      const holdReason = row.original?.holdReason;
      return (
        <div className='d-flex align-items-center flex-wrap'>
          <span className='mr-2'>{getStatusLabel(value)}</span>
          {value === 'on_hold' && holdReason ? <span className='text-muted mr-2'>({holdReason})</span> : null}
          {atRisk?.isAtRisk && (
            <Tooltip title={atRisk.reasonLabel || 'At Risk'} arrow>
              <div className='d-inline-flex align-items-center' style={{ transform: 'translateY(1px)' }}>
                <Badge color='danger' pill className='mb-0' style={{ cursor: 'default', lineHeight: 1.2 }}>
                  At Risk
                </Badge>
              </div>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    Header: 'Site Readiness',
    accessor: 'siteReadinessEvaluation',
    minCellWidth: '150px',
    filterKey: 'service_ticket.siteReadinessStatus',
    Cell: ({ value }: CellProps<ServiceTicketType, ServiceTicketType['siteReadinessEvaluation']>) => {
      const status = value?.status ?? SiteReadinessEvaluationStatus.NotStarted;
      return (
        <Badge color={getSiteReadinessStatusColor(status)} pill>
          {getSiteReadinessStatusLabel(status)}
        </Badge>
      );
    },
  },

  {
    Header: 'Created At',
    accessor: 'createdAt',
    minCellWidth: '150px',
    filterKey: 'service_ticket.createdAt',
    Cell: ({ value }: any) => formatDateTime(value),
  },
];

export const checklistTemplateListColumns: Array<ColumnType<ChecklistTemplateType>> = [
  {
    Header: 'Name',
    accessor: 'name',
    filterKey: 'obj.name',
  },
  {
    Header: 'Service variant',
    accessor: 'serviceVariant',
    filterKey: 'obj.serviceVariant',
    Cell: ({ row }: CellProps<ChecklistTemplateType, ChecklistTemplateType['serviceVariant']>) =>
      formatChecklistTemplateServiceVariant(row.original.serviceVariant),
  },
  {
    Header: 'Items',
    accessor: 'items',
    filterKey: 'obj.items',
    Cell: ({ row }: CellProps<ChecklistTemplateType, ChecklistTemplateType['items']>) =>
      row.original.items?.length ?? 0,
  },
  {
    Header: 'Default',
    accessor: 'isDefault',
    filterKey: 'obj.isDefault',
    Cell: ({ value }: CellProps<ChecklistTemplateType, boolean>) => (value ? 'Yes' : 'No'),
  },
  {
    Header: 'Active',
    accessor: 'isActive',
    filterKey: 'obj.isActive',
    Cell: ({ value }: CellProps<ChecklistTemplateType, boolean>) => (value ? 'Yes' : 'No'),
  },
  {
    Header: ' ',
    requiredGlobalScope: 'serviceCall_checklistTemplate_delete',
    Cell: () => <CustomCell type={'remove'} />,
  },
];
